import React, { useState, useRef, useEffect } from 'react';
import styles from './CharacterSelectorBar.module.css';
import { theaterMasksIcon } from '../../assets/icons';
import { ScenarioCharacter } from '../../api/types/scenario';
import { GrUserAdd } from "react-icons/gr";
import { IoMdClose } from "react-icons/io";

interface CharacterSelectorBarProps {
  characters: ScenarioCharacter[];
  selectedCharacter: ScenarioCharacter | null;
  onSelectCharacter: (character: ScenarioCharacter | null) => void;
  onRemoveCharacter?: (character: ScenarioCharacter) => void;
  onAddRandomCharacter?: () => void;
  isLoading?: boolean;
  isAutoMode?: boolean;
  isWaitingForAutoResponse?: boolean;
}

function CharacterSelectorBar({ 
  characters, 
  selectedCharacter, 
  onSelectCharacter,
  onRemoveCharacter,
  onAddRandomCharacter,
  isLoading = false,
  isAutoMode = false,
  isWaitingForAutoResponse = false
}: CharacterSelectorBarProps): React.ReactElement {
  // selectedCharacter can be null (unselected) or { id: null } (narrator) or { id: "some-id" } (character)
  const isNarratorSelected = selectedCharacter === null;
  
  // State to track which character is being long-pressed
  const [longPressedCharacter, setLongPressedCharacter] = useState<string | null>(null);
  
  // Flag to track if we're in long press mode to prevent character selection
  const [isLongPressMode, setIsLongPressMode] = useState(false);
  
  // Refs for long press timer
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const longPressDuration = 500; // 500ms for long press
  
  // Track touch start time for better mobile experience
  const touchStartTimeRef = useRef<number>(0);
  const touchMoveCountRef = useRef<number>(0);
  
  // Ref for the container element
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Ref for the character list element
  const characterListRef = useRef<HTMLDivElement>(null);
  
  // State to track which character is being removed (for animation)
  const [removingCharacterId, setRemovingCharacterId] = useState<string | null>(null);
  
  // Ref to track characters that are in the process of being removed
  const removingCharactersRef = useRef<Set<string>>(new Set());
  
  // State to track visible characters (including those being animated out)
  const [visibleCharacters, setVisibleCharacters] = useState<ScenarioCharacter[]>(characters);
  
  // Update visible characters when the characters prop changes
  useEffect(() => {
    // Keep any characters that are being removed in the visible list
    const removingIds = Array.from(removingCharactersRef.current);
    const removingCharacters = visibleCharacters.filter(c => 
      removingIds.includes(c.id) && !characters.some(newC => newC.id === c.id)
    );
    
    setVisibleCharacters([...characters, ...removingCharacters]);
  }, [characters]);
  
  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);
  
  // Add global click handler to exit delete mode when clicking outside
  useEffect(() => {
    const handleGlobalClick = (e: MouseEvent) => {
      // If we're in long press mode and the click is outside our component
      if (isLongPressMode && containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setLongPressedCharacter(null);
        setIsLongPressMode(false);
      }
    };
    
    const handleGlobalTouch = (e: TouchEvent) => {
      // If we're in long press mode and the touch is outside our component
      if (isLongPressMode && containerRef.current) {
        // Check if the touch is outside our component
        const touch = e.touches[0];
        const touchTarget = document.elementFromPoint(touch.clientX, touch.clientY);
        
        if (!containerRef.current.contains(touchTarget)) {
          setLongPressedCharacter(null);
          setIsLongPressMode(false);
        }
      }
    };
    
    // Add the event listeners
    document.addEventListener('click', handleGlobalClick);
    document.addEventListener('touchstart', handleGlobalTouch);
    
    // Clean up
    return () => {
      document.removeEventListener('click', handleGlobalClick);
      document.removeEventListener('touchstart', handleGlobalTouch);
    };
  }, [isLongPressMode]);
  
  // Add scroll handler to exit delete mode when scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (isLongPressMode) {
        setLongPressedCharacter(null);
        setIsLongPressMode(false);
      }
    };
    
    const listElement = characterListRef.current;
    if (listElement) {
      listElement.addEventListener('scroll', handleScroll);
    }
    
    // Clean up
    return () => {
      if (listElement) {
        listElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isLongPressMode]);
  
  // Handle long press start
  const handleLongPressStart = (characterId: string) => {
    if (isLoading) return;
    
    longPressTimerRef.current = setTimeout(() => {
      setLongPressedCharacter(characterId);
      setIsLongPressMode(true); // Set long press mode flag
    }, longPressDuration);
  };
  
  // Handle long press end
  const handleLongPressEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    // Don't reset long press mode here to allow clicking the remove button
  };
  
  // Handle touch start for better mobile experience
  const handleTouchStart = (characterId: string) => {
    if (isLoading) return;
    
    touchStartTimeRef.current = Date.now();
    touchMoveCountRef.current = 0;
    handleLongPressStart(characterId);
  };
  
  // Handle touch move to detect scrolling
  const handleTouchMove = () => {
    touchMoveCountRef.current += 1;
    
    // If user is scrolling, cancel long press
    if (touchMoveCountRef.current > 3) {
      handleLongPressEnd();
    }
  };
  
  // Handle touch end for better tap detection
  const handleTouchEnd = (character: ScenarioCharacter) => {
    handleLongPressEnd();
    
    // If it was a quick tap (not a long press) and not scrolling
    const tapDuration = Date.now() - touchStartTimeRef.current;
    if (tapDuration < longPressDuration && touchMoveCountRef.current < 3 && !isLongPressMode) {
      handleCharacterClick(character);
    }
  };
  
  // Handle character click
  const handleCharacterClick = (character: ScenarioCharacter) => {
    // Only select character if not in long press mode
    if (!isLoading && !isLongPressMode) {
      onSelectCharacter(character);
    }
  };
  
  // Handle character removal
  const handleRemoveCharacter = (e: React.MouseEvent, character: ScenarioCharacter) => {
    e.stopPropagation(); // Prevent triggering the character selection
    
    if (onRemoveCharacter) {
      // Set the character as removing to trigger animation
      setRemovingCharacterId(character.id);
      
      // Add to the set of characters being removed
      removingCharactersRef.current.add(character.id);
      
      // Wait for animation to complete before actually removing
      setTimeout(() => {
        // Call the actual removal function
        onRemoveCharacter(character);
        
        // Clean up after a short delay to ensure the animation is complete
        setTimeout(() => {
          setRemovingCharacterId(null);
          removingCharactersRef.current.delete(character.id);
          
          // Update visible characters to remove this one
          setVisibleCharacters(prev => prev.filter(c => c.id !== character.id));
          
          setLongPressedCharacter(null);
          setIsLongPressMode(false); // Reset long press mode after removal
        }, 50); // Small additional delay to ensure smooth transition
      }, 350); // Slightly shorter than animation duration for smoother transition
    }
  };
  
  // Handle add random character
  const handleAddRandomCharacter = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling
    if (onAddRandomCharacter && !isLoading) {
      onAddRandomCharacter();
    }
  };
  
  // Reset long press mode when clicking outside
  const handleContainerClick = (e: React.MouseEvent) => {
    // Only reset if clicking the container directly, not a child element
    if (e.currentTarget === e.target && isLongPressMode) {
      setLongPressedCharacter(null);
      setIsLongPressMode(false);
    }
  };
  
  return (
    <div 
      ref={containerRef}
      className={`${styles.container} 
        ${isLoading ? styles.loading : ''} 
        ${isWaitingForAutoResponse ? styles.waitingForAutoResponse : ''}`}
      onClick={handleContainerClick}
    >
      <div className={styles.characterList} ref={characterListRef}>
        <button
          className={`${styles.characterButton}`}
          disabled={isLoading || isWaitingForAutoResponse}
          aria-label="Add character"
          onClick={handleAddRandomCharacter}
          title="Add a random character from available characters"
          data-index="0"
        >
          <GrUserAdd size={20} />
        </button>

        <button
          className={`${styles.characterButton} ${isNarratorSelected ? styles.selected : ''}`}
          onClick={() => !isLoading && !isLongPressMode && !isWaitingForAutoResponse && onSelectCharacter(null)}
          disabled={isLoading || isWaitingForAutoResponse}
          data-index="1"
        >
          <div className={styles.characterAvatar}>
            <img src={theaterMasksIcon} alt="Narrator" />
          </div>
          <span className={styles.characterName}>Narrator</span>
        </button>

        {visibleCharacters.map((character, index) => (
          <button
            key={character.id}
            className={`${styles.characterButton} 
              ${selectedCharacter?.id === character.id ? styles.selected : ''} 
              ${longPressedCharacter === character.id ? styles.longPressed : ''}
              ${removingCharacterId === character.id ? styles.characterRemoving : ''}`}
            onClick={() => handleCharacterClick(character)}
            onMouseDown={() => handleLongPressStart(character.id)}
            onMouseUp={handleLongPressEnd}
            onMouseLeave={handleLongPressEnd}
            onTouchStart={() => handleTouchStart(character.id)}
            onTouchMove={handleTouchMove}
            onTouchEnd={() => handleTouchEnd(character)}
            onTouchCancel={handleLongPressEnd}
            disabled={isLoading || removingCharacterId === character.id || isWaitingForAutoResponse}
            style={removingCharactersRef.current.has(character.id) ? { position: 'relative', zIndex: 0 } : undefined}
            data-index={index + 2}
          >
            {longPressedCharacter === character.id && onRemoveCharacter && !removingCharactersRef.current.has(character.id) && (
              <div 
                className={styles.removeButton} 
                onClick={(e) => handleRemoveCharacter(e, character)}
                style={{ position: 'absolute', top: '-8px', right: '-8px', zIndex: 100 }}
              >
                <IoMdClose size={16} />
              </div>
            )}
            <div className={styles.characterAvatar}>
              <img src={character.icon} alt={character.display_name} />
            </div>
            <span className={styles.characterName}>{character.display_name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default CharacterSelectorBar; 